// src/components/dashboard/RecentEstimates.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaFileInvoiceDollar, 
  FaUser, 
  FaCalendarAlt, 
  FaEye, 
  FaPen,
  FaPaperPlane,
  FaExternalLinkAlt
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/utils';

const EstimatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const EstimateItem = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EstimateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const EstimateId = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-size: ${props => props.theme.typography.fontSize.medium};
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const EstimateDate = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
`;

const EstimateContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const ServicesList = styled.div`
  margin-top: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(1)};
  
  .service {
    background-color: ${props => props.theme.colors.background};
    border: 1px solid ${props => props.theme.colors.inputGray}33;
    border-radius: 12px;
    padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  }
`;

const EstimateFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(1)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const EstimatePrice = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.success};
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 12px;
  background-color: ${props => getStatusColor(props.status)}22;
  color: ${props => getStatusColor(props.status)};
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}22;
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-top: ${props => props.theme.spacing(2)};
  align-self: center;
  
  &:hover {
    opacity: 0.9;
  }
  
  svg {
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

// Helper to get color based on status
const getStatusColor = (status) => {
  switch (status) {
    case 'Draft':
      return '#9e9e9e'; // Gray
    case 'Sent':
      return '#2196f3'; // Blue
    case 'Viewed':
      return '#f9a825'; // Amber
    case 'Accepted':
      return '#4caf50'; // Green
    case 'Declined':
      return '#f44336'; // Red
    case 'Expired':
      return '#795548'; // Brown
    default:
      return '#9e9e9e';
  }
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const RecentEstimates = ({ data, loading, error }) => {
  const navigate = useNavigate();

  const handleEstimateClick = (estimateId) => {
    navigate(`/estimates/view/${estimateId}`);
  };

  const handleEditClick = (e, estimateId) => {
    e.stopPropagation();
    navigate(`/estimates/edit/${estimateId}`);
  };

  const handleViewClick = (e, estimateId) => {
    e.stopPropagation();
    navigate(`/estimates/view/${estimateId}`);
  };

  const handleSendClick = (e, estimateId) => {
    e.stopPropagation();
    // Navigate to send page or trigger send modal
    navigate(`/estimates/send/${estimateId}`);
  };

  return (
    <DashboardCard
      title="Recent Estimates"
      icon={<FaFileInvoiceDollar />}
      viewAllLink="/estimates"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <EstimatesWrapper>
          {data.map((estimate) => (
            <EstimateItem 
              key={estimate.id} 
              onClick={() => handleEstimateClick(estimate.id)}
            >
              <EstimateHeader>
                <EstimateId>
                  <FaFileInvoiceDollar />
                  Estimate #{estimate.id}
                </EstimateId>
                <EstimateDate>
                  <FaCalendarAlt />
                  {formatDate(estimate.createdAt)}
                </EstimateDate>
              </EstimateHeader>
              
              <EstimateContent>
                <CustomerInfo>
                  <FaUser />
                  {estimate.customer_name || estimate.Lead?.name || 'No Customer'}
                </CustomerInfo>
                
                {estimate.services && estimate.services.length > 0 && (
                  <ServicesList>
                    {estimate.services.slice(0, 3).map((service, index) => (
                      <span key={index} className="service">
                        {service.service_name}
                      </span>
                    ))}
                    {estimate.services.length > 3 && (
                      <span className="service">
                        +{estimate.services.length - 3} more
                      </span>
                    )}
                  </ServicesList>
                )}
                
                <EstimateFooter>
                  <EstimatePrice>
                    {formatCurrency(estimate.totalPrice)}
                  </EstimatePrice>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StatusBadge status={estimate.status}>
                      {estimate.status}
                    </StatusBadge>
                    
                    <ActionsRow>
                      {estimate.status === 'Draft' && (
                        <ActionButton 
                          onClick={(e) => handleEditClick(e, estimate.id)}
                          title="Edit"
                        >
                          <FaPen />
                        </ActionButton>
                      )}
                      
                      <ActionButton 
                        onClick={(e) => handleViewClick(e, estimate.id)}
                        title="View"
                      >
                        <FaEye />
                      </ActionButton>
                      
                      {estimate.status === 'Draft' && (
                        <ActionButton 
                          onClick={(e) => handleSendClick(e, estimate.id)}
                          title="Send"
                        >
                          <FaPaperPlane />
                        </ActionButton>
                      )}
                    </ActionsRow>
                  </div>
                </EstimateFooter>
              </EstimateContent>
            </EstimateItem>
          ))}
          
          <ViewAllButton onClick={() => navigate('/estimates')}>
            View All Estimates
            <FaExternalLinkAlt />
          </ViewAllButton>
        </EstimatesWrapper>
      ) : (
        <EmptyState>No recent estimates found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default RecentEstimates;